import React, { FC, Fragment, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { IconDefinition, faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cardsShadow } from '../../../styleHelpers/mixins/shadow';
import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { useDropdown } from '../../../tools/hooks';
import { MultilineTextWithEllipsis } from '../TextWithEllipsis/MultilineTextWithEllipsis';

const Wrapper = styled.div<{
    openFromRight?: boolean;
    distanceFromTop?: string;
    customWidth?: number;
    $viewportFixed?: { top?: string; bottom?: string; left?: string; right?: string };
}>`
    border-radius: 8px;
    background: ${colorStack.white};
    ${cardsShadow()};
    min-width: 220px;
    ${props => props.$viewportFixed ? css`
        position: fixed;
        z-index: 100050;
        top: ${props.$viewportFixed.top ?? 'auto'};
        bottom: ${props.$viewportFixed.bottom ?? 'auto'};
        left: ${props.$viewportFixed.left ?? 'auto'};
        right: ${props.$viewportFixed.right ?? 'auto'};
    ` : css`
        position: absolute;
        z-index: 999;
        ${props.distanceFromTop && css`
            top: ${props.distanceFromTop};
        `}
        ${props.openFromRight && css`
            right: 0;
        `};
    `}
    ${props => props.customWidth && css`
        width: ${props.customWidth}px;
    `};
    > *:first-child {
        border-radius: 8px 8px 0 0;
    };
    > *:last-child {
        border-radius: 0 0 8px 8px;
    };
`;

const Link = styled.button<{ withIcon: boolean }>`
    color: ${colorStack.content};
    border: none;
    background: none;
    ${fontSizeAndHeight[16]};
    padding: .5rem;
    font-weight: 400;
    width: 100%;
    text-align: left;
    transition: all .3s;
    cursor: pointer;
    margin: 0!important;
    white-space: nowrap;
    display: flex;
    gap: 1rem;
    svg {
        transition: all .3s;
    }
    div {
        width: 100%;
        ${props => props.withIcon && css`
            width: calc(100% - 16px);
        `};
    }
    &[disabled],
    &[disabled]:hover {
        transition: none;
        cursor: not-allowed;
        color: ${colorStack.disabled};
    }

    &:hover {
        color: ${colorStack.middleBlue};
    }
`;

const ExpandedElem = styled.div`
    color: ${colorStack.content};
    ${fontSizeAndHeight[16]};
    padding: .5rem;
    font-weight: 400;
    text-align: left;
    transition: all .3s;
    cursor: pointer;
    margin: 0!important;
    white-space: nowrap;
    display: flex;
    gap: 1rem;
    align-items: center;
    svg {
        transition: all .3s;
    }
    &:hover {
        color: ${colorStack.middleBlue};
    }
    > div {
        left: 100%;
        top: 0
    }
`;

export interface ISingleLink {
    name?: React.ReactNode;
    visible: boolean;
    disabled?: boolean;
    ico?: IconDefinition;
    dataLc?: string;
    icoColor?: string;
    customRender?: React.ReactNode;
    links?: ISingleLink[];
    action?(e?);
}

export interface IProps {
    links: ISingleLink[];
    customWidth?: number;
    openFromRight?: boolean;
    distanceFromTop?: string;
    /** When set, menu is positioned in viewport (e.g. with createPortal) to escape overflow/stacking from ancestors */
    viewportFixed?: { top?: string; bottom?: string; left?: string; right?: string };
    onClickOutside?(e): void;
}

const SingleLinkElem: FC<React.PropsWithChildren<ISingleLink>> = props => {
    const [wrapperRef, dropdownOpen, toggleDropdown, closeDropdown] = useDropdown();

    const onMouseLeaveElement = useCallback(() => {
        setTimeout(() => {
            closeDropdown();
        }, 100);
    }, [closeDropdown]);

    return (
        <>
            {props.links?.length > 0 ? (
                <ExpandedElem ref={wrapperRef} onMouseEnter={toggleDropdown} onMouseLeave={onMouseLeaveElement}>
                    {props.ico && <FontAwesomeIcon icon={props.ico} color={props.icoColor} />}
                    {props.name}
                    <FontAwesomeIcon icon={faCaretRight} />
                    {dropdownOpen &&
                        <Wrapper>
                            {props.links.map((link, index) =>
                                <SingleLinkElem {...link} key={index} data-lc={link.dataLc} disabled={props.disabled} />
                            )}
                        </Wrapper>
                    }
                </ExpandedElem>
            ) : (
                <>
                    {props.customRender ? (
                        <>
                            {props.customRender}
                        </>
                    ) : (

                        <Link onClick={props.action} type="button" data-lc={props.dataLc} disabled={props.disabled} withIcon={!!props.ico}>
                            {props.ico && <FontAwesomeIcon icon={props.ico} color={props.icoColor} />}
                            <MultilineTextWithEllipsis numberOfLines={1} >
                                {props.name}
                            </MultilineTextWithEllipsis>
                        </Link>
                    )}
                </>
            )}
        </>
    );
};

export const ContextMenu: FC<React.PropsWithChildren<IProps>> = props => {
    const wrappedOnClickOutside = useCallback(() => {
        return props.onClickOutside ? () => {
            setTimeout(props.onClickOutside, 100);
        } : undefined;
    }, [props.onClickOutside]);

    useEffect(() => {
        if (wrappedOnClickOutside) {
            document.addEventListener('click', wrappedOnClickOutside, true);
        }
        return () => {
            if (wrappedOnClickOutside) {
                document.removeEventListener('click', wrappedOnClickOutside, true);
            }
        };
    }, [wrappedOnClickOutside]);

    return (
        <Wrapper
            openFromRight={props.openFromRight}
            distanceFromTop={props.distanceFromTop}
            customWidth={props.customWidth}
            $viewportFixed={props.viewportFixed}
        >
            {props.links?.filter(elem => elem.visible)?.map((link, index) =>
                <Fragment key={index}>
                    {link.customRender ? (
                        <>
                            {link.customRender}
                        </>
                    ) : (
                        <SingleLinkElem {...link} />
                    )}
                </Fragment>
            )}
        </Wrapper>
    );
};
